import { SalesOrder, SalesOrderItem } from 'services/salesOrders';
import { CustomField, CustomFieldValue } from 'services/customFields/types';
import {
  LabelElement,
  isTextElement,
  isBarcodeElement,
  isQRCodeElement,
  QRCodeElementProps,
  PageSize,
} from './types';
import { EDGE_OFFSET_BOTTOM, EDGE_OFFSET_RIGHT } from './consts';

interface Tag {
  name: string;
}

export const MONEY_FIELDS = [
  'amount',
  'price',
  'total',
  'subtotal',
  'tax',
  'discount',
  'unitPrice',
  'lineTotal',
  'balance',
  'payment',
  'charge',
  'fee',
  'adjustment',
  'grandTotal',
];

export const formatCurrency = (value: any): string => {
  const num = Number.parseFloat(String(value));
  if (
    isNaN(num) ||
    value === null ||
    value === undefined ||
    String(value).trim() === ''
  ) {
    return '$0.00';
  }
  return `$${num.toFixed(2)}`;
};

// Assuming CustomField and CustomFieldValue types are imported or available in this scope
// import { CustomField, CustomFieldValue } from './types'; // Example import

const resolveCustomFieldValueFromObject = (
  dataObject: SalesOrder | SalesOrderItem,
  fullPath: string // e.g., "customFields.ActualCustomFieldName"
): any | undefined => {
  // 'any' for flexibility, as CustomFieldValue can be varied
  if (
    !dataObject ||
    typeof dataObject !== 'object' ||
    !('customFields' in dataObject) ||
    !Array.isArray((dataObject as any).customFields)
  ) {
    return undefined;
  }

  const customFieldName = fullPath.split('customFields.')[1];
  if (!customFieldName) {
    return undefined; // Malformed path for custom field
  }

  const customFieldsArray = (dataObject as any).customFields as CustomField[];
  const customField = customFieldsArray.find(
    (cf) => cf && cf.name === customFieldName
  );

  if (
    customField &&
    customField.customFieldValues !== undefined &&
    customField.customFieldValues !== null
  ) {
    return customField.customFieldValues;
  }

  return undefined;
};

export const getSOValueAtPath = (
  dataObject: SalesOrder | SalesOrderItem,
  path: string
): string | undefined => {
  // If the path is exactly 'customFields', it refers to the array of custom field definitions,
  // not a singular displayable value. The expectation is to return undefined for such a path.
  if (path === 'customFields') {
    return undefined;
  }

  let resolvedValue: any = path
    .split('.')
    .reduce((acc: any, currentKey: string) => {
      if (acc && typeof acc === 'object' && currentKey in acc) {
        return acc[currentKey];
      }
      return undefined;
    }, dataObject);

  // Handle special case for tags
  if (path.endsWith('saleItem.tags') && Array.isArray(resolvedValue)) {
    return resolvedValue.map((tag: Tag) => tag.name).join(', ');
  }

  if (resolvedValue === undefined && path.includes('customFields.')) {
    resolvedValue = resolveCustomFieldValueFromObject(dataObject, path);
  }

  if (resolvedValue !== undefined && resolvedValue !== null) {
    const formatValueToString = (val: CustomFieldValue | any): string => {
      if (Array.isArray(val)) {
        return val.map(String).join(', ');
      }
      if (val instanceof Date) {
        return val.toLocaleDateString();
      }
      if (typeof val === 'boolean') {
        return String(val);
      }
      return String(val);
    };
    return formatValueToString(resolvedValue);
  }

  return undefined;
};

export const getResolvedDisplayValue = (
  element: LabelElement,
  previewMode: boolean,
  previewData: SalesOrder | null
): string => {
  const fieldKey = element.dynamicField;

  if (previewMode && fieldKey && previewData) {
    const value = getSOValueAtPath(previewData, fieldKey);
    if (value === null || value === undefined || String(value).trim() === '') {
      return ''; // Return empty string for unresolved fields in preview
    }
    return String(value);
  }

  if (fieldKey) {
    return `{${fieldKey}}`;
  }

  if (isTextElement(element)) {
    return element.text || 'Text';
  }
  if (isBarcodeElement(element)) {
    return element.barcodeValue || 'Sample';
  }
  if (isQRCodeElement(element)) {
    return (element as QRCodeElementProps).qrValue || 'Sample QR Code';
  }
  return 'Text';
};

/**
 * Handles the movement of elements with constraints
 * @param primaryElement The main element being moved
 * @param elementsToMove All elements that should be moved
 * @param allElements All elements in the canvas
 * @param deltaX The desired X movement
 * @param deltaY The desired Y movement
 * @param pageSize The current page size
 * @param pages Total number of pages
 * @param hasFooter Whether footer is enabled
 * @param footerH Footer height
 * @returns Object containing updated elements and updated selected elements
 */
export const calculateTranslatedElements = (
  primaryElement: LabelElement,
  elementsToMove: LabelElement[],
  allElements: LabelElement[],
  deltaX: number,
  deltaY: number,
  pageSize: PageSize,
  pages: number,
  hasFooter: boolean,
  footerH: number
) => {
  if (deltaX === 0 && deltaY === 0) {
    return {
      updatedElements: allElements,
      updatedSelectedElements: elementsToMove,
    };
  }

  if (elementsToMove.length > 1) {
    let finalDeltaX = deltaX;
    let finalDeltaY = deltaY;

    for (const element of elementsToMove) {
      if (element.id === primaryElement.id) continue;

      const elMaxX = pageSize.width - element.width - EDGE_OFFSET_RIGHT;
      const canvasHeight = pageSize.height * pages;
      let elMaxY = canvasHeight - element.height - EDGE_OFFSET_BOTTOM;

      if (hasFooter && element.type === 'advancedTable') {
        elMaxY = canvasHeight - footerH - element.height - EDGE_OFFSET_BOTTOM;
      }

      const maxDeltaLeft = -element.x;
      const maxDeltaRight = elMaxX - element.x;
      const maxDeltaUp = -element.y;
      const maxDeltaDown = elMaxY - element.y;

      if (deltaX < 0) {
        finalDeltaX = Math.max(finalDeltaX, maxDeltaLeft);
      } else if (deltaX > 0) {
        finalDeltaX = Math.min(finalDeltaX, maxDeltaRight);
      }

      if (deltaY < 0) {
        finalDeltaY = Math.max(finalDeltaY, maxDeltaUp);
      } else if (deltaY > 0) {
        finalDeltaY = Math.min(finalDeltaY, maxDeltaDown);
      }
    }

    const updatedSelectedElements = elementsToMove.map((element) => {
      return {
        ...element,
        x: element.x + finalDeltaX,
        y: element.y + finalDeltaY,
      };
    });

    const updatedSelectedElementsById = updatedSelectedElements.reduce(
      (acc, element) => {
        acc[element.id] = element;
        return acc;
      },
      {} as Record<string, LabelElement>
    );

    const updatedElements = allElements.map((element) => {
      const isSelected = elementsToMove.some((sel) => sel.id === element.id);
      if (isSelected) {
        return updatedSelectedElementsById[element.id];
      }
      return element;
    });

    return {
      updatedElements,
      updatedSelectedElements,
    };
  } else {
    const updatedElement = {
      ...primaryElement,
      x: primaryElement.x + deltaX,
      y: primaryElement.y + deltaY,
    };

    const updatedElements = allElements.map((element) =>
      element.id === primaryElement.id ? updatedElement : element
    );

    return {
      updatedElements,
      updatedSelectedElements: [updatedElement],
    };
  }
};

export const calculateConstrainedPosition = (
  dropX: number,
  dropY: number,
  elementWidth: number,
  elementHeight: number,
  canvasWidth: number,
  canvasHeight: number,
  footerHeight: number
): { finalX: number; finalY: number } => {
  const constrainedCenterX = Math.max(
    elementWidth / 2,
    Math.min(dropX, canvasWidth - elementWidth / 2)
  );
  const constrainedCenterY = Math.max(
    elementHeight / 2,
    Math.min(dropY, canvasHeight - elementHeight / 2 - footerHeight)
  );

  const finalX = constrainedCenterX - elementWidth / 2;
  const finalY = constrainedCenterY - elementHeight / 2;

  return { finalX, finalY };
};
